import { useCallback, useEffect, useRef, useState } from 'react';
/**
 * Thin wrapper around the browser's SpeechRecognition API (when available).
 * We normalize transcript + confidence, exposing a single `transcribe` call
 * that returns a promise resolving to the best-guess string.
 */
export function useSpeechRecognition(lang = 'en-US') {
    const [state, setState] = useState('idle');
    const [error, setError] = useState(null);
    const recRef = useRef(null);
    useEffect(() => {
        if (typeof window === 'undefined')
            return;
        const Ctor = window.SpeechRecognition ?? window.webkitSpeechRecognition;
        if (!Ctor) {
            setState('unsupported');
            return;
        }
        const rec = new Ctor();
        rec.lang = lang;
        rec.interimResults = false;
        rec.continuous = false;
        rec.maxAlternatives = 3;
        recRef.current = rec;
        return () => {
            try {
                rec.abort();
            }
            catch {
                // ignore
            }
        };
    }, [lang]);
    const transcribe = useCallback(async () => {
        const rec = recRef.current;
        if (!rec) {
            setState('unsupported');
            return '';
        }
        setError(null);
        setState('listening');
        return new Promise((resolve, reject) => {
            let finished = false;
            const safeStop = () => {
                if (finished)
                    return;
                finished = true;
                try {
                    rec.stop();
                }
                catch {
                    // ignore
                }
            };
            rec.onresult = (ev) => {
                if (finished)
                    return;
                finished = true;
                const first = ev.results[0]?.[0];
                const text = first?.transcript ?? '';
                setState('processing');
                resolve(text);
            };
            rec.onerror = (ev) => {
                if (finished)
                    return;
                finished = true;
                setState('error');
                setError(ev.error);
                reject(new Error(ev.error));
            };
            rec.onend = () => {
                if (!finished) {
                    finished = true;
                    setState('idle');
                    resolve('');
                }
            };
            try {
                rec.start();
            }
            catch (err) {
                setState('error');
                reject(err);
            }
            // Hard timeout
            setTimeout(safeStop, 10_000);
        });
    }, []);
    const abort = useCallback(() => {
        try {
            recRef.current?.abort();
        }
        catch {
            // ignore
        }
        setState('idle');
    }, []);
    return { transcribe, abort, state, error };
}
export async function evaluatePronunciation(target, transcript) {
    const res = await fetch('/api/v1/pronunciation/evaluate', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ target, transcript }),
    });
    if (!res.ok)
        throw new Error(`eval ${res.status}`);
    const body = (await res.json());
    return body.data;
}
//# sourceMappingURL=useSpeechRecognition.js.map